import React, { useEffect, useState } from 'react';
import api from '../services/api';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  BarChart3, TrendingUp, Camera, Package, Users, CheckCircle,
  Calendar, Activity, Zap, DollarSign, Clock, Filter
} from 'lucide-react';

const COLORS = {
  amber: '#F59E0B', blue: '#3B82F6', green: '#10B981', red: '#EF4444',
  purple: '#8B5CF6', cyan: '#06B6D4', pink: '#EC4899', gray: '#64748B',
};
const CHART_COLORS = [COLORS.amber, COLORS.blue, COLORS.green, COLORS.purple, COLORS.cyan, COLORS.pink, COLORS.red];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#1E293B', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.4)', fontSize: 12
    }}>
      <p style={{ color: '#94A3B8', marginBottom: 4 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || COLORS.amber, fontWeight: 600 }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString('es-ES') : p.value}
        </p>
      ))}
    </div>
  );
};

export default function Analytics() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [days, setDays] = useState(30);
  const [kpis, setKpis] = useState(null);
  const [photoActivity, setPhotoActivity] = useState([]);
  const [taskDist, setTaskDist] = useState(null);
  const [equipSummary, setEquipSummary] = useState(null);
  const [productivity, setProductivity] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [comparison, setComparison] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.data || r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = selectedProject ? { project_id: selectedProject } : {};
    Promise.all([
      api.get('/analytics/kpis', { params }),
      api.get('/analytics/photo-activity', { params: { ...params, days } }),
      api.get('/analytics/task-distribution', { params }),
      api.get('/analytics/equipment-summary', { params }),
      api.get('/analytics/productivity', { params: { ...params, days } }),
      api.get('/analytics/milestones', { params }),
      api.get('/analytics/project-comparison'),
    ].map(p => p.catch(() => ({ data: null }))))
      .then(([kR, phR, tR, eR, pR, mR, cR]) => {
        setKpis(kR.data);
        setPhotoActivity(phR.data || []);
        setTaskDist(tR.data);
        setEquipSummary(eR.data);
        setProductivity(pR.data);
        setMilestones(mR.data);
        setComparison(cR.data || []);
      })
      .finally(() => setLoading(false));
  }, [selectedProject, days]);

  const tabs = [
    { id: 'overview', label: 'Resumen', icon: <BarChart3 size={16} /> },
    { id: 'photos', label: 'Fotos', icon: <Camera size={16} /> },
    { id: 'tasks', label: 'Tareas', icon: <CheckCircle size={16} /> },
    { id: 'equipment', label: 'Equipos', icon: <Package size={16} /> },
    { id: 'productivity', label: 'Productividad', icon: <Users size={16} /> },
    { id: 'financial', label: 'Financiero', icon: <DollarSign size={16} /> },
    { id: 'comparison', label: 'Comparativa', icon: <TrendingUp size={16} /> },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Analytics</h1>
          <p className="subtitle">Métricas detalladas y tendencias</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Filter size={16} style={{ color: 'var(--text-muted)' }} />
          <select className="filter-select" value={selectedProject}
            onChange={e => setSelectedProject(e.target.value)}>
            <option value="">Todos los proyectos</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
          </select>
        </div>
        <select className="filter-select" value={days} onChange={e => setDays(parseInt(e.target.value))}>
          <option value={7}>Últimos 7 días</option>
          <option value={14}>Últimos 14 días</option>
          <option value={30}>Últimos 30 días</option>
          <option value={60}>Últimos 60 días</option>
          <option value={90}>Últimos 90 días</option>
        </select>
      </div>

      {/* Tabs */}
      <div className="tabs-bar">
        {tabs.map(tab => (
          <button key={tab.id} className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-screen"><div className="spinner" /></div>
      ) : (
        <>
          {activeTab === 'overview' && <OverviewTab kpis={kpis} photoActivity={photoActivity} taskDist={taskDist} />}
          {activeTab === 'photos' && <PhotosTab data={photoActivity} days={days} />}
          {activeTab === 'tasks' && <TasksTab data={taskDist} />}
          {activeTab === 'equipment' && <EquipmentTab data={equipSummary} />}
          {activeTab === 'productivity' && <ProductivityTab data={productivity} days={days} />}
          {activeTab === 'financial' && <FinancialTab data={milestones} />}
          {activeTab === 'comparison' && <ComparisonTab data={comparison} />}
        </>
      )}
    </div>
  );
}

// ============================================
// Tab Components
// ============================================

function OverviewTab({ kpis, photoActivity, taskDist }) {
  const today = kpis?.today || {};
  const week = kpis?.week || {};
  const trends = kpis?.trends || {};
  const avgs = kpis?.averages || {};

  const summaryCards = [
    { label: 'Fotos hoy', value: today.photos_today || 0, icon: <Camera size={20} />, color: 'purple', trend: trends.photos },
    { label: 'Tareas hoy', value: today.tasks_completed_today || 0, icon: <CheckCircle size={20} />, color: 'green', trend: trends.tasks },
    { label: 'Equipos hoy', value: today.equipment_today || 0, icon: <Package size={20} />, color: 'blue' },
    { label: 'Usuarios activos', value: today.active_users_today || 0, icon: <Users size={20} />, color: 'amber' },
    { label: 'Fotos/semana', value: week.photos_week || 0, icon: <Calendar size={20} />, color: 'cyan' },
    { label: 'Media fotos/día', value: parseFloat(avgs.avg_photos_per_day || 0).toFixed(1), icon: <Activity size={20} />, color: 'pink' },
  ];

  const photoChartData = (photoActivity || []).map(d => ({
    date: new Date(d.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
    total: parseInt(d.total),
  }));

  const taskStatusData = (taskDist?.byStatus || []).map(s => ({
    name: statusLabel(s.status), value: parseInt(s.count), status: s.status
  }));

  return (
    <>
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        {summaryCards.map((c, i) => (
          <div className="kpi-card kpi-card-sm" key={i}>
            <div className={`kpi-icon ${c.color}`}>{c.icon}</div>
            <div className="kpi-data">
              <span className="kpi-value kpi-value-sm">{c.value}</span>
              <span className="kpi-label">{c.label}</span>
            </div>
            {c.trend !== undefined && c.trend !== 0 && (
              <span className={`kpi-trend ${c.trend > 0 ? 'up' : 'down'}`}>
                {c.trend > 0 ? '↑' : '↓'} {Math.abs(c.trend)}%
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="dashboard-grid" style={{ marginTop: 20 }}>
        <ChartCard title="Tendencia de fotos" icon={<Camera size={18} />}>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={photoChartData}>
              <defs>
                <linearGradient id="aGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.amber} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS.amber} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 10 }} />
              <YAxis tick={{ fill: '#64748B', fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="total" stroke={COLORS.amber} fill="url(#aGrad)" name="Fotos" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Estado de tareas" icon={<CheckCircle size={18} />}>
          {taskStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={taskStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                  paddingAngle={3} dataKey="value" strokeWidth={0}>
                  {taskStatusData.map((d, i) => (
                    <Cell key={i} fill={statusColor(d.status)} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="empty-text">Sin datos</p>}
        </ChartCard>
      </div>
    </>
  );
}

function PhotosTab({ data, days }) {
  const chartData = (data || []).map(d => ({
    date: new Date(d.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
    total: parseInt(d.total),
    instalación: parseInt(d.installation || 0),
    avance: parseInt(d.progress || 0),
    defecto: parseInt(d.defect || 0),
    entrega: parseInt(d.delivery || 0),
    general: parseInt(d.general || 0),
    usuarios: parseInt(d.unique_users || 0),
  }));

  const totalPhotos = chartData.reduce((s, d) => s + d.total, 0);
  const avgPerDay = chartData.length > 0 ? (totalPhotos / chartData.length).toFixed(1) : 0;
  const maxDay = chartData.reduce((m, d) => d.total > m.total ? d : m, { total: 0, date: '-' });

  return (
    <>
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginBottom: 20 }}>
        <MiniKpi label={`Total (${days}d)`} value={totalPhotos} />
        <MiniKpi label="Media/día" value={avgPerDay} />
        <MiniKpi label="Día pico" value={`${maxDay.total} (${maxDay.date})`} />
      </div>

      <ChartCard title="Fotos por día y tipo" icon={<Camera size={18} />} full>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
            <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 10 }} />
            <YAxis tick={{ fill: '#64748B', fontSize: 10 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="instalación" name="Instalación" stackId="a" fill={COLORS.blue} />
            <Bar dataKey="avance" name="Avance" stackId="a" fill={COLORS.green} />
            <Bar dataKey="defecto" name="Defecto" stackId="a" fill={COLORS.red} />
            <Bar dataKey="entrega" name="Entrega" stackId="a" fill={COLORS.purple} />
            <Bar dataKey="general" name="General" stackId="a" fill={COLORS.gray} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Usuarios únicos por día" icon={<Users size={18} />} full style={{ marginTop: 20 }}>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
            <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 10 }} />
            <YAxis tick={{ fill: '#64748B', fontSize: 10 }} />
            <Tooltip content={<CustomTooltip />} />
            <Line type="monotone" dataKey="usuarios" name="Usuarios" stroke={COLORS.cyan} strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </>
  );
}

function TasksTab({ data }) {
  const byStatus = (data?.byStatus || []).map(s => ({
    name: statusLabel(s.status), value: parseInt(s.count), status: s.status
  }));
  const byCategory = (data?.byCategory || []).map(c => ({
    name: c.code || c.category || '?',
    fullName: c.category,
    total: parseInt(c.total),
    completed: parseInt(c.completed),
    progress: parseFloat(c.avg_progress) || 0,
  }));
  const completionRate = (data?.completionRate || []).map(d => ({
    date: new Date(d.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
    completadas: parseInt(d.completed),
  }));

  return (
    <div className="dashboard-grid">
      <ChartCard title="Por estado" icon={<CheckCircle size={18} />}>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie data={byStatus} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
              paddingAngle={3} dataKey="value" strokeWidth={0}>
              {byStatus.map((d, i) => <Cell key={i} fill={statusColor(d.status)} />)}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Por categoría" icon={<BarChart3 size={18} />}>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={byCategory} layout="vertical" barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
            <XAxis type="number" tick={{ fill: '#64748B', fontSize: 10 }} />
            <YAxis type="category" dataKey="name" tick={{ fill: '#94A3B8', fontSize: 10 }} width={50} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="completed" name="Completadas" fill={COLORS.green} radius={[0, 4, 4, 0]} />
            <Bar dataKey="total" name="Total" fill={COLORS.blue} opacity={0.3} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Tareas completadas/día (30d)" icon={<TrendingUp size={18} />} full>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={completionRate}>
            <defs>
              <linearGradient id="compGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS.green} stopOpacity={0.3} />
                <stop offset="95%" stopColor={COLORS.green} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
            <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 10 }} />
            <YAxis tick={{ fill: '#64748B', fontSize: 10 }} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="completadas" stroke={COLORS.green} fill="url(#compGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function EquipmentTab({ data }) {
  if (!data) return <p className="empty-text">Sin datos de equipos</p>;

  const byType = (data.byType || []).map(e => ({
    name: equipLabel(e.type), total: parseInt(e.count),
    instalado: parseInt(e.installed || 0), operativo: parseInt(e.operational || 0),
    en_obra: parseInt(e.in_site || 0), almacén: parseInt(e.in_warehouse || 0),
  }));

  const byStatus = (data.byStatus || []).map(s => ({
    name: statusLabel(s.status), value: parseInt(s.count), status: s.status
  }));

  return (
    <div className="dashboard-grid">
      <ChartCard title="Por tipo" icon={<Package size={18} />}>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={byType}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
            <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 10 }} />
            <YAxis tick={{ fill: '#64748B', fontSize: 10 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="instalado" name="Instalado" stackId="a" fill={COLORS.green} />
            <Bar dataKey="operativo" name="Operativo" stackId="a" fill={COLORS.amber} />
            <Bar dataKey="en_obra" name="En obra" stackId="a" fill={COLORS.blue} />
            <Bar dataKey="almacén" name="Almacén" stackId="a" fill={COLORS.gray} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Por estado" icon={<Activity size={18} />}>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie data={byStatus} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
              paddingAngle={3} dataKey="value" strokeWidth={0}>
              {byStatus.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>

      {data.recentInstallations?.length > 0 && (
        <div className="card" style={{ gridColumn: '1 / -1', marginTop: 8 }}>
          <div className="card-header"><h3><Clock size={18} /> Últimas instalaciones</h3></div>
          <div className="card-body">
            <table className="data-table">
              <thead><tr>
                <th>Tipo</th><th>Marca/Modelo</th><th>S/N</th><th>Proyecto</th><th>Instalado por</th><th>Fecha</th>
              </tr></thead>
              <tbody>
                {data.recentInstallations.map((e, i) => (
                  <tr key={i}>
                    <td>{equipLabel(e.type)}</td>
                    <td>{[e.brand, e.model].filter(Boolean).join(' ') || '-'}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{e.serial_number || '-'}</td>
                    <td>{e.project_code}</td>
                    <td>{e.installed_by_name || '-'}</td>
                    <td>{e.installed_date ? new Date(e.installed_date).toLocaleDateString('es-ES') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductivityTab({ data, days }) {
  if (!data) return <p className="empty-text">Sin datos de productividad</p>;

  const photosByUser = (data.photosByUser || []).slice(0, 10);
  const tasksByUser = (data.tasksByUser || []).slice(0, 10);

  // Heatmap data
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const heatmapData = {};
  (data.activityHeatmap || []).forEach(h => {
    const key = `${h.day_of_week}-${h.hour}`;
    heatmapData[key] = parseInt(h.count);
  });
  const maxHeat = Math.max(1, ...Object.values(heatmapData));

  return (
    <>
      <div className="dashboard-grid">
        <ChartCard title={`Fotos por usuario (${days}d)`} icon={<Camera size={18} />}>
          <ResponsiveContainer width="100%" height={Math.max(200, photosByUser.length * 32)}>
            <BarChart data={photosByUser} layout="vertical" barSize={18}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis type="number" tick={{ fill: '#64748B', fontSize: 10 }} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#94A3B8', fontSize: 11 }} width={120} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="photos" name="Fotos" fill={COLORS.purple} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={`Tareas completadas por usuario (${days}d)`} icon={<CheckCircle size={18} />}>
          <ResponsiveContainer width="100%" height={Math.max(200, tasksByUser.length * 32)}>
            <BarChart data={tasksByUser} layout="vertical" barSize={18}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis type="number" tick={{ fill: '#64748B', fontSize: 10 }} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#94A3B8', fontSize: 11 }} width={120} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="tasks_completed" name="Tareas" fill={COLORS.green} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Activity Heatmap */}
      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-header"><h3><Activity size={18} /> Mapa de actividad (hora del día)</h3></div>
        <div className="card-body" style={{ overflowX: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '60px repeat(24, 1fr)', gap: 2, minWidth: 600 }}>
            <div />
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} style={{ textAlign: 'center', fontSize: 9, color: '#64748B' }}>{h}h</div>
            ))}
            {dayNames.map((day, d) => (
              <React.Fragment key={d}>
                <div style={{ fontSize: 11, color: '#94A3B8', display: 'flex', alignItems: 'center' }}>{day}</div>
                {Array.from({ length: 24 }, (_, h) => {
                  const val = heatmapData[`${d}-${h}`] || 0;
                  const intensity = val / maxHeat;
                  return (
                    <div key={h} title={`${day} ${h}:00 — ${val} fotos`} style={{
                      height: 22, borderRadius: 3,
                      background: val > 0
                        ? `rgba(245, 158, 11, ${0.15 + intensity * 0.85})`
                        : '#1E293B',
                    }} />
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function FinancialTab({ data }) {
  if (!data) return <p className="empty-text">Sin datos financieros</p>;

  const s = data.summary || {};
  const totalBillable = parseFloat(s.total_billable) || 0;
  const totalReceived = parseFloat(s.total_received) || 0;
  const pendingInvoice = parseFloat(s.pending_invoice) || 0;
  const collectionRate = totalBillable > 0 ? ((totalReceived / totalBillable) * 100).toFixed(1) : 0;

  const byProject = (data.byProject || []).map(p => ({
    name: p.code,
    facturado: parseFloat(p.received) || 0,
    pendiente: (parseFloat(p.total_value) || 0) - (parseFloat(p.received) || 0),
  }));

  return (
    <>
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: 20 }}>
        <MiniKpi label="Total facturable" value={`${formatCurrency(totalBillable)} €`} />
        <MiniKpi label="Total cobrado" value={`${formatCurrency(totalReceived)} €`} color="green" />
        <MiniKpi label="Pendiente facturar" value={`${formatCurrency(pendingInvoice)} €`} color="amber" />
        <MiniKpi label="Tasa de cobro" value={`${collectionRate}%`} color="blue" />
        <MiniKpi label="Hitos completados" value={`${s.completed || 0}/${s.total || 0}`} />
        <MiniKpi label="Hitos vencidos" value={s.overdue || 0} color="red" />
      </div>

      <div className="dashboard-grid">
        <ChartCard title="Facturación por proyecto" icon={<DollarSign size={18} />} full>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={byProject}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 10 }} />
              <YAxis tick={{ fill: '#64748B', fontSize: 10 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip />} formatter={v => `${formatCurrency(v)} €`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="facturado" name="Cobrado" stackId="a" fill={COLORS.green} />
              <Bar dataKey="pendiente" name="Pendiente" stackId="a" fill={COLORS.amber} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </>
  );
}

function ComparisonTab({ data }) {
  if (!data?.length) return <p className="empty-text">Sin proyectos para comparar</p>;

  const chartData = data.map(p => ({
    name: p.code,
    progreso: Math.round(p.progress_percentage || 0),
    tareas: parseInt(p.total_tasks) || 0,
    completadas: parseInt(p.completed_tasks) || 0,
    fotos: parseInt(p.total_photos) || 0,
    equipos: parseInt(p.total_equipment) || 0,
    kwp: parseFloat(p.plant_capacity_kwp) || 0,
  }));

  // Radar data for top 5 projects
  const radarData = data.slice(0, 5).map(p => {
    const taskPct = p.total_tasks > 0 ? (p.completed_tasks / p.total_tasks * 100) : 0;
    const msPct = p.total_milestones > 0 ? (p.completed_milestones / p.total_milestones * 100) : 0;
    const equipPct = p.total_equipment > 0 ? (p.installed_equipment / p.total_equipment * 100) : 0;
    return {
      name: p.code,
      'Progreso': Math.round(p.progress_percentage || 0),
      'Tareas (%)': Math.round(taskPct),
      'Hitos (%)': Math.round(msPct),
      'Equipos (%)': Math.round(equipPct),
    };
  });

  return (
    <>
      <ChartCard title="Progreso de proyectos" icon={<TrendingUp size={18} />} full>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
            <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 10 }} />
            <YAxis tick={{ fill: '#64748B', fontSize: 10 }} domain={[0, 100]} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="progreso" name="Progreso (%)" fill={COLORS.amber} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="dashboard-grid" style={{ marginTop: 20 }}>
        <ChartCard title="Volumen de trabajo" icon={<BarChart3 size={18} />}>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 10 }} />
              <YAxis tick={{ fill: '#64748B', fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="fotos" name="Fotos" fill={COLORS.purple} />
              <Bar dataKey="equipos" name="Equipos" fill={COLORS.cyan} />
              <Bar dataKey="completadas" name="Tareas OK" fill={COLORS.green} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Radar comparativo (top 5)" icon={<Activity size={18} />}>
          <ResponsiveContainer width="100%" height={250}>
            <RadarChart data={[
              { metric: 'Progreso', ...Object.fromEntries(radarData.map(p => [p.name, p['Progreso']])) },
              { metric: 'Tareas', ...Object.fromEntries(radarData.map(p => [p.name, p['Tareas (%)']])) },
              { metric: 'Hitos', ...Object.fromEntries(radarData.map(p => [p.name, p['Hitos (%)']])) },
              { metric: 'Equipos', ...Object.fromEntries(radarData.map(p => [p.name, p['Equipos (%)']])) },
            ]}>
              <PolarGrid stroke="#334155" />
              <PolarAngleAxis dataKey="metric" tick={{ fill: '#94A3B8', fontSize: 11 }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#64748B', fontSize: 9 }} />
              {radarData.map((p, i) => (
                <Radar key={p.name} name={p.name} dataKey={p.name}
                  stroke={CHART_COLORS[i]} fill={CHART_COLORS[i]} fillOpacity={0.15} />
              ))}
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </RadarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Summary table */}
      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-header"><h3><Zap size={18} /> Resumen comparativo</h3></div>
        <div className="card-body" style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead><tr>
              <th>Código</th><th>Proyecto</th><th>kWp</th><th>Progreso</th>
              <th>Tareas</th><th>Fotos</th><th>Equipos</th><th>Hitos</th><th>Ingresos</th>
            </tr></thead>
            <tbody>
              {data.map((p, i) => (
                <tr key={i}>
                  <td><span className="project-code">{p.code}</span></td>
                  <td>{p.name}</td>
                  <td>{p.plant_capacity_kwp || 0}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className="progress-bar" style={{ width: 60 }}>
                        <div className="progress-fill" style={{ width: `${p.progress_percentage || 0}%` }} />
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{Math.round(p.progress_percentage || 0)}%</span>
                    </div>
                  </td>
                  <td>{p.completed_tasks}/{p.total_tasks}</td>
                  <td>{p.total_photos}</td>
                  <td>{p.total_equipment}</td>
                  <td>{p.completed_milestones}/{p.total_milestones}</td>
                  <td style={{ fontWeight: 600, color: COLORS.green }}>{formatCurrency(p.revenue_collected || 0)} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ============================================
// Shared Components
// ============================================

function ChartCard({ title, icon, children, full, style }) {
  return (
    <div className="card" style={{ ...(full ? { gridColumn: '1 / -1' } : {}), ...style }}>
      <div className="card-header"><h3>{icon} {title}</h3></div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function MiniKpi({ label, value, color }) {
  return (
    <div className="kpi-card kpi-card-sm">
      <div className="kpi-data" style={{ textAlign: 'center', width: '100%' }}>
        <span className="kpi-value kpi-value-sm" style={color ? { color: COLORS[color] } : {}}>{value}</span>
        <span className="kpi-label">{label}</span>
      </div>
    </div>
  );
}

function statusLabel(s) {
  const labels = {
    completed: 'Completadas', in_progress: 'En progreso', pending: 'Pendientes',
    blocked: 'Bloqueadas', overdue: 'Vencidas', in_warehouse: 'Almacén',
    in_site: 'En obra', installed: 'Instalado', operational: 'Operativo',
  };
  return labels[s] || s || '?';
}

function statusColor(s) {
  return { completed: COLORS.green, in_progress: COLORS.blue, pending: COLORS.gray,
    blocked: COLORS.red, overdue: COLORS.red }[s] || COLORS.gray;
}

function equipLabel(t) {
  return { module: 'Módulo FV', inverter: 'Inversor', structure: 'Estructura',
    transformer: 'Transformador', meter: 'Medidor', cable: 'Cable',
    tracker: 'Tracker', other: 'Otro' }[t] || t || '?';
}

function formatCurrency(n) {
  return Number(n || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 });
}
